import { StateCreator } from 'zustand';
import { Pipeline, PipelineData } from '../types';
import { Node, Edge } from '@xyflow/react';
import { EdgeData } from '../types';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('pipelineSlice');

export interface PipelineSlice {
  // State
  pipelines: Pipeline[];
  activePipelineId: string | null;

  // Actions
  addPipeline: (id: string, name: string, type?: 'pipeline' | 'workflow') => void;
  setActivePipeline: (id: string | null) => void;
  renamePipeline: (id: string, newName: string) => void;
  closePipeline: (id: string) => void;
  createPipeline: (id: string, data: PipelineData, type?: 'pipeline' | 'workflow') => void;
  updatePipeline: (id: string, data: Partial<Pipeline>) => void;
  deletePipeline: (id: string) => void;
  migratePipelineId: (oldId: string, newId: string, newName?: string) => void;
  markStructuralChange: (pipelineId: string) => void;
  clearStructuralChanges: (pipelineId: string) => void;
}

export const createPipelineSlice: StateCreator<PipelineSlice> = (set) => ({
  // Initial state
  pipelines: [],
  activePipelineId: null,

  // Actions
  addPipeline: (id, name, type = 'pipeline') =>
    set((state) => ({
      pipelines: [
        ...state.pipelines,
        {
          id,
          name,
          type,
          nodes: [],
          edges: [],
          selectedNodeId: null,
          lastNodePosition: { x: 0, y: 0 },
          isNodeDoubleClicked: false,
          hasStructuralChanges: false,
        },
      ],
      activePipelineId: id,
    })),

  setActivePipeline: (id) => set({ activePipelineId: id }),

  renamePipeline: (id, newName) =>
    set((state) => ({
      pipelines: state.pipelines.map((p) => (p.id === id ? { ...p, name: newName } : p)),
    })),

  closePipeline: (id) =>
    set((state) => ({
      pipelines: state.pipelines.filter((p) => p.id !== id),
      activePipelineId: state.activePipelineId === id ? null : state.activePipelineId,
    })),

  createPipeline: (id, data, type = 'pipeline') => {
    // Convert transforms to ReactFlow nodes
    const nodes: Node[] = 'transforms' in data && Array.isArray(data.transforms) 
      ? data.transforms.map((transform: any) => ({
          id: transform.id || transform.name,
          type: transform.type || 'customNode',
          position: transform.position || { x: 0, y: 0 },
          data: transform.data || {},
          width: 48,
          height: 48,
        }))
      : [];

    // Convert hops to ReactFlow edges
    const edges: Edge[] = 'hops' in data && Array.isArray(data.hops)
      ? data.hops.map((hop: any) => ({
          id: `${hop.from}-${hop.to}`,
          source: hop.from,
          target: hop.to,
          type: 'custom', // Must match the type registered in edgeTypes.ts
          data: {
            condition: 'unconditional' as const,
            enabled: hop.enabled !== false,
            fields: [],
            hopType: hop.hopType,
          } as EdgeData,
        }))
      : [];

    const newPipeline: Pipeline = {
      id,
      name: 'name' in data ? data.name : 'New Pipeline',
      description: ('description' in data && typeof data.description === 'string') ? data.description : undefined,
      type,
      nodes,
      edges,
      selectedNodeId: null,
      lastNodePosition: { x: 0, y: 0 },
      isNodeDoubleClicked: false,
      hasStructuralChanges: false,
    };
    
    set((state) => {
      // Check if pipeline already exists (could be from persisted localStorage)
      const existingIndex = state.pipelines.findIndex((p) => p.id === id);
      
      if (existingIndex !== -1) {
        // Replace existing pipeline with fresh data
        logger.debug(`Replacing existing ${type} with fresh data from backend`, { 
          id, 
          name: newPipeline.name,
          oldNodeCount: state.pipelines[existingIndex].nodes.length,
          newNodeCount: nodes.length
        });
        
        const updatedPipelines = [...state.pipelines];
        updatedPipelines[existingIndex] = newPipeline;
        
        return {
          pipelines: updatedPipelines,
          activePipelineId: id,
        };
      } else {
        // Add new pipeline
        logger.debug(`Creating new ${type} with ${nodes.length} nodes and ${edges.length} edges`, { 
          id, 
          name: newPipeline.name 
        });
        
        return {
          pipelines: [...state.pipelines, newPipeline],
          activePipelineId: id,
        };
      }
    });
  },

  updatePipeline: (id, data) =>
    set((state) => ({
      pipelines: state.pipelines.map((p) => (p.id === id ? { ...p, ...data } : p)),
    })),

  deletePipeline: (id) =>
    set((state) => ({
      pipelines: state.pipelines.filter((p) => p.id !== id),
      activePipelineId: state.activePipelineId === id ? null : state.activePipelineId,
    })),

  migratePipelineId: (oldId, newId, newName) =>
    set((state) => ({
      pipelines: state.pipelines.map((p) =>
        p.id === oldId ? { ...p, id: newId, ...(newName && { name: newName }) } : p
      ),
      activePipelineId: state.activePipelineId === oldId ? newId : state.activePipelineId,
    })),

  markStructuralChange: (pipelineId) =>
    set((state) => ({
      pipelines: state.pipelines.map((p) =>
        p.id === pipelineId ? { ...p, hasStructuralChanges: true } : p
      ),
    })),

  clearStructuralChanges: (pipelineId) =>
    set((state) => ({
      pipelines: state.pipelines.map((p) =>
        p.id === pipelineId ? { ...p, hasStructuralChanges: false } : p
      ),
    })),
});

